<template>
	<div class="copy-field" :class="containerClass">
		<slot name="target" />
		<button
			:class="buttonClasses"
			type="button"
			@click="copy"
			aria-label="Скопировать"
		>
			<slot name="button-content" />
		</button>
	</div>
</template>

<script>
export default {
	name: 'FormCopyField',

	props: {
		containerClass: {
			type: String,
			default: ''
		},
		buttonText: {
			type: String,
			default: 'Скопировать'
		},
		successText: {
			type: String,
			default: 'Скопировано!'
		},
		errorText: {
			type: String,
			default: 'Ошибка'
		},
		showText: {
			type: Boolean,
			default: true
		},
		autoReset: {
			type: Boolean,
			default: true
		},
		resetDelay: {
			type: Number,
			default: 3000
		}
	},

	data() {
		return {
			status: '' // '', 'success', 'error'
		};
	},

	computed: {
		buttonLabel() {
			if (this.status === 'success') return this.successText;
			if (this.status === 'error') return this.errorText;
			return this.buttonText;
		},

		buttonClasses() {
			return {
				'copy-field__button': true,
				'copy-field__button--copied': this.status === 'success',
				'copy-field__button--error': this.status === 'error'
			};
		}
	},

	methods: {
		/**
		 * Извлекает текст из любого узла, включая вложенные
		 * @param {Node} node
		 * @returns {string}
		 */
		getTextFromNode(node) {
			if (!node) return '';
			if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim();
			if (node.nodeType === Node.ELEMENT_NODE) {
				return Array.from(node.childNodes)
					.map(child => this.getTextFromNode(child))
					.join(' ')
					.trim();
			}
			return '';
		},

		/**
		 * Находит элемент слота и извлекает из него текст
		 * @returns {string}
		 */
		getTargetText() {
			// Ищем элемент, куда вставлен слот
			const slotElement = this.$el.querySelector('[data-copy-target]') || this.$el.firstElementChild;

			if (slotElement) {
				return this.getTextFromNode(slotElement);
			}

			return '';
		},

		copy() {
			const text = this.getTargetText();
			if (!text) {
				this.handleFailure();
				return;
			}

			const isSecure = window.location.protocol === 'https:';
			const onSuccess = () => this.handleSuccess();
			const onFailure = () => this.handleFailure();

			if (isSecure && navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard
					.writeText(text)
					.then(onSuccess)
					.catch(() => {
						this.fallbackCopy(text) ? onSuccess() : onFailure();
					});
			} else {
				this.fallbackCopy(text) ? onSuccess() : onFailure();
			}
		},

		handleSuccess() {
			this.status = 'success';
			if (this.autoReset) {
				setTimeout(() => {
					if (this.status === 'success') this.status = '';
				}, this.resetDelay);
			}
		},

		handleFailure() {
			this.status = 'error';
			if (this.autoReset) {
				setTimeout(() => {
					if (this.status === 'error') this.status = '';
				}, this.resetDelay);
			}
		},

		fallbackCopy(text) {
			const textarea = document.createElement('textarea');
			textarea.value = text;
			textarea.style.position = 'fixed';
			textarea.style.top = '-9999px';
			textarea.style.opacity = '0';

			document.body.appendChild(textarea);
			textarea.focus();
			textarea.select();

			let success = false;
			try {
				success = document.execCommand('copy');
			} catch (e) {
				console.error('Fallback copy failed:', e);
			}

			document.body.removeChild(textarea);
			return success;
		}
	}
};
</script>
